/**
 * Browser OpenTelemetry bootstrap for Discovery Check SPA.
 *
 * Initialises a WebTracerProvider that:
 *  - Auto-instruments all fetch() calls (adds W3C traceparent header)
 *  - Captures document-load performance timing
 *  - Captures unhandled errors & promise rejections as error spans
 *  - Exports spans via OTLP/JSON to the backend proxy at /api/v1/telemetry/traces
 *
 * Call initTelemetry() once in main.jsx before React renders.
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { trace, SpanStatusCode } from '@opentelemetry/api';

let _initialised = false;
let _tracer = null;

/**
 * Initialise browser OpenTelemetry.  Safe to call more than once.
 */
export function initTelemetry() {
  if (_initialised) return;
  _initialised = true;

  const buildId = import.meta.env.VITE_BUILD_ID || 'unknown';
  const version = import.meta.env.VITE_APP_VERSION || '0.0.0';
  const environment = import.meta.env.VITE_ENVIRONMENT || 'dev';

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: 'discovery-check-spa',
    [ATTR_SERVICE_VERSION]: version,
    'deployment.environment': environment,
    'browser.build_id': buildId,
  });

  // Export spans to the backend OTLP proxy (same origin → no CORS issues)
  const exporter = new OTLPTraceExporter({
    url: `${window.location.origin}/api/v1/telemetry/traces`,
    headers: {},
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        maxQueueSize: 100,
        maxExportBatchSize: 30,
        scheduledDelayMillis: 5000,
      }),
    ],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // Auto-instrument fetch, document load, and user interactions
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Only trace calls to our own API (not Firebase, CDNs, etc.)
        ignoreUrls: [/firebaseapp\.com/, /googleapis\.com/, /gstatic\.com/, /accounts\.google\.com/],
        propagateTraceHeaderCorsUrls: [/.*/],  // Propagate traceparent on all same-origin
        clearTimingResources: true,
      }),
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation({
        eventNames: ['click'],
      }),
    ],
  });

  _tracer = trace.getTracer('discovery-check-spa', version);

  // ── Global error capture ──────────────────────────────────────
  window.addEventListener('error', (event) => {
    if (!_tracer) return;
    const span = _tracer.startSpan('browser.error', {
      attributes: {
        'error.type': 'unhandled',
        'error.message': event.message || 'Unknown error',
        'error.filename': event.filename || '',
        'error.lineno': event.lineno || 0,
        'error.colno': event.colno || 0,
        'browser.url': window.location.href,
        'browser.user_agent': navigator.userAgent,
      },
    });
    span.setStatus({ code: SpanStatusCode.ERROR, message: event.message });
    span.end();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!_tracer) return;
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : '';
    const span = _tracer.startSpan('browser.unhandled_rejection', {
      attributes: {
        'error.type': 'unhandled_rejection',
        'error.message': message,
        'error.stack': stack ? stack.substring(0, 4096) : '',
        'browser.url': window.location.href,
      },
    });
    span.setStatus({ code: SpanStatusCode.ERROR, message });
    span.end();
  });

  // eslint-disable-next-line no-console
  console.log(`[OTel] Browser telemetry initialised (build=${buildId}, env=${environment})`);
}

/**
 * Get the global browser tracer for creating custom spans.
 *
 * Usage:
 *   import { getTracer } from './telemetry';
 *   const span = getTracer().startSpan('my.operation');
 *   try { ... } finally { span.end(); }
 */
export function getTracer() {
  if (!_tracer) {
    _tracer = trace.getTracer('discovery-check-spa');
  }
  return _tracer;
}
